import { mount, unmount, setAttr, setChildren } from 'redom';

import application from 'application';
import serverInterface from 'server-interface';

import internalEventCodes from 'event-system/codes/internal';

import VideoHorizontalFlipProcessor from './video-horizontal-flip-processor';
import MeetingRoom from './meeting-room';
import UploadingRecordingView from './uploading-recording-view';

import {
  delay
} from 'utilities';
import * as meetingUtilities from './utilities';

class AttendeeMode {

  constructor(currentUserId, meeting, post, meetingRoomOptions = { outputAudio: true }) {

    this.meetingRoom = null;

    this.meeting = meeting;
    this.post = post;
    this.attendee = null;
    this.currentUserId = currentUserId;
    this.meetingRoomOptions = meetingRoomOptions;

    this.uploadingRecordingView = new UploadingRecordingView();

    this.boundLeaveMeeting = this.leaveMeeting.bind(this);

  }

  isMeetingOwner() {

    return this.currentUserId === this.meeting.ownerId;

  }

  update(meeting) {

    this.meeting = meeting;

    if (this.meetingRoom === null) {
      return;
    }

    this.meetingRoom.update(this.meeting);

  }

  displayUnknownError(message = 'Unknown error') {

    application.eventSystem.internal.emit(internalEventCodes.DISPLAY_SYSTEM_TOAST_MESSAGE, {
      message,
      seconds: 5,
      type: 'error'
    });
    application.eventSystem.internal.emit(internalEventCodes.NAVIGATE_TO, {
      to: `/community/${this.post.humanFriendlyId}`
    });

  }

  async startMeeting(controlOptions, isAttendee) {

    const meeting = this.meeting;
    const postId = meeting.postId;

    try {

      this.attendee = await serverInterface.startup.community.singleProducerMeeting.createOrGetSingleProducerMeetingAttendeeByPostIdAndAttendeeId(postId, meeting.data.MeetingId);

    } catch (exception) {
      // I need to clean this up. Due to competing calls, this could happen. 
      // So, simply making the call again will resolve the situation.
      if (exception.code === 'SINGLE_PRODUCER_MEETING_MEMBER_ALREADY_EXISTS') {
        try {

          this.attendee = await serverInterface.startup.community.singleProducerMeeting.createOrGetSingleProducerMeetingAttendeeByPostIdAndAttendeeId(postId, meeting.data.MeetingId);

        } catch (exception) {
          this.displayUnknownError();
          return false;
        }
      } else {

        if (meetingUtilities.isSingleProducerMeetingSlotsAvailableError(exception) === true) {
          this.displayUnknownError('Unable to join streaming. No slots available.');
          return false;
        }

        this.displayUnknownError('Live streaming expired! This could happen if meeting was not published for long time.');
        return false;
      }
    }

    await this.initializeMeetingSession();

    this.mountMeetingRoom(controlOptions);

    return true;

  }

  async initializeMeetingSession() {

    const {
      ConsoleLogger,
      DefaultDeviceController,
      DefaultMeetingSession,
      LogLevel,
      MeetingSessionConfiguration
    } = await import('amazon-chime-sdk-js');

    const logger = new ConsoleLogger('HelpTapLogger', LogLevel.ERROR);
    const deviceController = new DefaultDeviceController(logger);

    const meetingResponse = this.meeting.data;
    const attendeeResponse = this.attendee.data;
    const configuration = new MeetingSessionConfiguration(meetingResponse, attendeeResponse);

    // In the usage examples below, you will use this meetingSession object.
    this.meetingSession = new DefaultMeetingSession(configuration, logger, deviceController);

    await this.tryInitializeAudioVideoStreams();

  }

  async tryInitializeAudioVideoStreams() {

    const { ConsoleLogger, LogLevel, DefaultVideoTransformDevice } = await import('amazon-chime-sdk-js');

    const meetingSession = this.meetingSession;

    const audioInputDevices = await meetingSession.audioVideo.listAudioInputDevices();
    const audioOutputDevices = await meetingSession.audioVideo.listAudioOutputDevices();

    if (audioOutputDevices.length > 0) {
      await meetingSession.audioVideo.chooseAudioOutput(audioOutputDevices[0].deviceId);
    }

    if (audioInputDevices.length > 0) {

      this.audioStream = await this.meetingSession.audioVideo.startAudioInput(audioInputDevices[0].deviceId);
      this.meetingSession.audioVideo.realtimeMuteLocalAudio();
    }

    if (this.currentUserId !== this.meeting.ownerId) {
      return;
    }

    const videoInputDevices = await meetingSession.audioVideo.listVideoInputDevices();

    if (videoInputDevices.length > 0) {

      const videoHorizontalFlipProcessor = new VideoHorizontalFlipProcessor();

      await videoHorizontalFlipProcessor.load();

      const stages = [videoHorizontalFlipProcessor];
      const logger = new ConsoleLogger('HelpTapLogger', LogLevel.ERROR);
      const transformDevice = new DefaultVideoTransformDevice(logger, videoInputDevices[0].deviceId, stages);

      const maxWidth = 1280;
      const maxHeight = 720;
      const maxFramerate = 30;

      meetingSession.audioVideo.chooseVideoInputQuality(maxWidth, maxHeight, maxFramerate);
      await meetingSession.audioVideo.startVideoInput(transformDevice);
    }

  }

  mountMeetingRoom(controlOptions) {

    const meetingOwner = this.post.owner;

    this.meetingRoom = new MeetingRoom(meetingOwner, this.meeting, this.attendee, this.meetingSession, controlOptions, this.meetingRoomOptions);

    this.meetingRoom.el.addEventListener('leave-meeting', this.boundLeaveMeeting);

    setChildren(this.el, [this.meetingRoom]);

  }

  unmountMeetingRoom() {

    unmount(this, this.meetingRoom);

  }

  async leaveMeeting(event) {

    try {

      this.meetingRoom.muteLocal();

      await delay(100);

      await this.meetingSession.audioVideo.stopAudioInput();
      console.log('If this log line is not present, microphone is not released for the viewer');
      if (this.isMeetingOwner() === true) {

        await this.meetingSession.audioVideo.stopVideoInput();

      } else {

        await this.meetingSession.audioVideo.pauseVideoTile(this.meetingRoom.ownerVideoTileId);
        await this.meetingSession.audioVideo.pauseVideoTile(this.meetingRoom.ownerScreenVideoTileId);

      }

      await this.meetingSession.audioVideo.stop();

      if (this.isMeetingOwner() === true) {

        await this.doLeaveMeeting();
        // Recording disabled temporarily
        // this.mountUploadingRecordingView();
        // await this.meetingRecorder.stop();
        // this.unmountUploadingRecordingView();

      }

      if (this.meetingRoom && this.meetingRoom.el) {
        this.meetingRoom.el.removeEventListener('leave-meeting', this.boundLeaveMeeting);
      }

      await this.meetingSession.destroy();

      this.audioStream = null;
      this.meetingSession = null;
      this.meetingRoom = null;

      this.mountNextView();

    } catch (exception) {

      setAttr(event.detail.button, { disabled: false });

    }

    console.log('Left');

  }

  releaseResources() {

    if (this.meetingRoom !== null) {
      this.meetingRoom.releaseResources();
    }

  }

  mountUploadingRecordingView() {
    mount(this.el, this.uploadingRecordingView);
  }

  unmountUploadingRecordingView() {
    unmount(this.el, this.uploadingRecordingView);
  }

}

export default AttendeeMode;